import {useEffect, useState} from 'react';
import {SubtitleStyle} from '@/models/Subtitle';

type Phase = 'original' | 'masked';

const PHASE_MS: Record<Phase, number> = {original: 1400, masked: 3300};
const NEXT_PHASE: Record<Phase, Phase> = {original: 'masked', masked: 'original'};

const LINES = [
    {original: '你怎么知道', translated: 'How did you know'},
    {original: '我在五星市场啊', translated: 'I was at the Five Star market?'},
];

type Props = {
    backgroundMode: SubtitleStyle['backgroundMode'];
};

export default function MaskingPreview({backgroundMode}: Props) {
    const [phase, setPhase] = useState<Phase>('original');
    const [shownMode, setShownMode] = useState(backgroundMode);
    const [reducedMotion, setReducedMotion] = useState(false);

    if (shownMode !== backgroundMode) {
        setShownMode(backgroundMode);
        setPhase('masked');
    }

    useEffect(() => {
        const query = window.matchMedia('(prefers-reduced-motion: reduce)');
        const sync = () => setReducedMotion(query.matches);
        sync();
        query.addEventListener('change', sync);
        return () => query.removeEventListener('change', sync);
    }, []);

    useEffect(() => {
        if (reducedMotion) return;
        const timer = window.setTimeout(() => setPhase(NEXT_PHASE[phase]), PHASE_MS[phase]);
        return () => window.clearTimeout(timer);
    }, [phase, shownMode, reducedMotion]);

    const masked = reducedMotion || phase === 'masked';
    const perLine = backgroundMode === 'per-line';

    return (
        <div aria-hidden className="overflow-hidden rounded-xl bg-slate-950 shadow-lg shadow-slate-900/20 ring-1 ring-black/10 dark:shadow-black/50 dark:ring-white/10">
            <div className="relative aspect-[2.2/1] overflow-hidden">
                <img src="/img/frame.webp" alt="" className="absolute inset-0 h-full w-full origin-center object-cover motion-safe:animate-hero-pan"/>

                <div className="absolute inset-x-0 bottom-[8%] flex justify-center px-4">
                    <div className="relative isolate flex flex-col items-center text-center text-[13px] font-medium leading-snug">
                        <Mask visible={masked && !perLine}/>
                        {LINES.map((line, index) => (
                            <div key={index} className="relative grid px-1.5 py-0.5">
                                <span className="text-white [grid-area:1/1] [text-shadow:0_0_4px_rgba(0,0,0,0.9),0_1px_2px_rgba(0,0,0,1)]">
                                    {line.original}
                                </span>
                                <Mask visible={masked && perLine}/>
                                <span className={`relative z-20 text-white transition-opacity duration-300 [grid-area:1/1] ${masked ? 'opacity-100' : 'opacity-0'}`}>
                                    {line.translated}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="px-3.5 py-3">
                <div className="relative h-1 rounded-full bg-white/15">
                    <div className="absolute inset-y-0 left-0 w-[62%] rounded-full bg-orange-500"/>
                    <div className="absolute left-[62%] top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-orange-500 ring-2 ring-slate-950"/>
                </div>
            </div>
        </div>
    );
}

function Mask({visible}: {visible: boolean}) {
    return (
        <span className={`absolute inset-0 z-10 bg-slate-900 transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}/>
    );
}
